import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box, Grid, Card, Stack, Typography, Chip, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Divider, LinearProgress, alpha, useTheme, Snackbar, Alert,
  ToggleButtonGroup, ToggleButton, CircularProgress, Skeleton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  FormControlLabel, Switch,
} from '@mui/material';
import { Plus, Megaphone, Users, X, Send, Calendar, AlertTriangle, Pause, Play, RotateCcw, Trash2, Download, Store, Package, UserCheck, Pencil, UserX } from 'lucide-react';
import {
  BROADCAST_STATUS, fmtDateTime,
} from './waData';
import WaSectionHeader, { waGridItem, useWaMobile, waDialogProps, waDialogActionsSx } from './WaLayout';
import { waApi } from './waApi';
import { onWaEvent } from './waSocket';
import { notify } from '../../utils/notificationService';
import WaTemplatePreview from './WaTemplatePreview';

// ISO timestamp → "YYYY-MM-DDTHH:mm" for a datetime-local input (local tz).
const toLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (e) { return ''; }
};

const StatCell = ({ label, value, color, onClick }) => {
  const clickable = typeof onClick === 'function';
  return (
    <Box onClick={clickable ? onClick : undefined}
      sx={{ textAlign: 'center', flex: 1, minWidth: 0, px: { xs: 0.25, sm: 0.5 },
        ...(clickable && { cursor: 'pointer', borderRadius: 1.5, transition: 'background-color .15s',
          '&:hover': { bgcolor: alpha(color, 0.1) } }) }}>
      <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 800, color, textDecoration: clickable ? 'underline' : 'none' }}>{value}</Typography>
      <Typography sx={{ fontSize: { xs: 9.5, sm: 10.5 }, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
    </Box>
  );
};

// CSV export (same escaping rules as ReportsView) so the admin can take the
// failed list into a spreadsheet to triage — nudge vs remove.
const FAILED_HEADERS = [
  { label: 'Name', value: (r) => r.name },
  { label: 'Phone', value: (r) => r.phone },
  { label: 'Business', value: (r) => r.businessName },
  { label: 'Businesses', value: (r) => r.businessCount },
  { label: 'Products', value: (r) => r.productCount },
  { label: 'Error code', value: (r) => r.errorCode },
  { label: 'Failure reason', value: (r) => r.errorReason },
];
const toCsv = (rows, headers) => {
  const head = headers.map((h) => h.label).join(',');
  const body = rows.map((r) => headers.map((h) => {
    const v = h.value(r);
    if (v == null) return '';
    const str = String(v).replace(/"/g, '""');
    return /[",\n]/.test(str) ? `"${str}"` : str;
  }).join(',')).join('\n');
  return `${head}\n${body}`;
};
const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// Who failed to receive this broadcast, enriched with profile completeness
// (business name + product count) so the admin can decide per-user: remind them
// to finish their profile, or drop them from future sends.
const FailedReportDialog = ({ broadcast, onClose, onRemoved }) => {
  const theme = useTheme();
  const mobile = useWaMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [alreadyMarkedCount, setAlreadyMarkedCount] = useState(0);
  const [confirming, setConfirming] = useState(false); // inline "are you sure" bar
  const [removing, setRemoving] = useState(false);
  const [removed, setRemoved] = useState(null);          // { marked, protectedSellers } once done

  useEffect(() => {
    if (!broadcast) return undefined;
    let alive = true;
    setLoading(true); setError(''); setRows([]); setAlreadyMarkedCount(0); setConfirming(false); setRemoving(false); setRemoved(null);
    waApi.getBroadcastFailed(broadcast.id)
      .then((data) => {
        if (alive) {
          setRows(Array.isArray(data?.rows) ? data.rows : []);
          setAlreadyMarkedCount(data?.alreadyMarkedCount || 0);
        }
      })
      .catch(() => { if (alive) setError('Could not load the failed-recipients report.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [broadcast]);

  // Profile-complete = has at least one business AND at least one product. These
  // are the ones worth keeping/reminding; empty-profile users are remove candidates.
  const withProfile = rows.filter((r) => r.businessCount > 0 || r.productCount > 0).length;
  // Only empty-profile failures are eligible to mark unreachable (real sellers are
  // protected — their failure is usually transient, not a dead number).
  const eligible = rows.length - withProfile;

  const markUnreachable = async () => {
    if (!broadcast) return;
    setRemoving(true);
    try {
      const res = await waApi.removeBroadcastFailed(broadcast.id);
      setRemoved(res || { marked: 0, protectedSellers: withProfile });
      setConfirming(false);
      const updated = await waApi.getBroadcastFailed(broadcast.id);
      if (updated) {
        setRows(Array.isArray(updated.rows) ? updated.rows : []);
        setAlreadyMarkedCount(updated.alreadyMarkedCount || 0);
      }
      onRemoved?.(res);
    } catch (e) {
      setError(e?.response?.data?.msg || 'Could not mark the failed recipients as unreachable.');
    } finally {
      setRemoving(false);
    }
  };

  const exportCsv = () => {
    const safe = (broadcast?.name || 'broadcast').replace(/[^\w-]+/g, '_');
    downloadCsv(`failed_${safe}.csv`, toCsv(rows, FAILED_HEADERS));
  };

  return (
    <Dialog open={!!broadcast} onClose={onClose} {...waDialogProps(mobile, 'md')}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="span" sx={{ fontSize: 16, fontWeight: 800 }}>Failed recipients</Typography>
          <Typography noWrap sx={{ fontSize: 12, color: 'text.disabled' }}>{broadcast?.name}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={28} /></Stack>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && rows.length === 0 && (
          <Stack alignItems="center" sx={{ py: 4 }} spacing={1}>
            {alreadyMarkedCount > 0 ? (
              <>
                <UserX size={28} color={theme.palette.success.main} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                  All {alreadyMarkedCount} failed contact{alreadyMarkedCount === 1 ? '' : 's'} already marked as unreachable
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.disabled', textAlign: 'center' }}>
                  They are opted out and tagged "Undeliverable" — future broadcasts will skip them.
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
                No failed recipients recorded for this broadcast.
              </Typography>
            )}
          </Stack>
        )}
        {!loading && !error && rows.length > 0 && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" gap={1}>
              <Chip size="small" label={`${rows.length} failed`} sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.error.main, 0.12), color: theme.palette.error.main }} />
              <Chip size="small" icon={<Store size={12} />} label={`${withProfile} have a business/products`}
                sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.main, '& .MuiChip-icon': { color: theme.palette.success.main } }} />
              <Chip size="small" label={`${eligible} empty profile`}
                sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.text.disabled, 0.12), color: 'text.secondary' }} />
              {alreadyMarkedCount > 0 && (
                <Chip size="small" icon={<UserX size={12} />} label={`${alreadyMarkedCount} already marked unreachable`}
                  sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.info.main, 0.10), color: theme.palette.info.main, '& .MuiChip-icon': { color: theme.palette.info.main } }} />
              )}
            </Stack>

            {rows.some((r) => r.errorCode === '131049' || (r.errorReason && r.errorReason.includes('pacing')) || (r.errorReason && r.errorReason.includes('marketing'))) && (
              <Alert severity="info" sx={{ mb: 1.5, fontSize: 12 }}>
                <b>Meta Marketing Pacing (Code #131049):</b> Meta dynamically caps how many promotional messages a WhatsApp user can receive from all businesses. These phone numbers are valid WhatsApp users, but Meta blocked delivery because the template is categorized as <b>MARKETING</b>. Switching to a <b>UTILITY</b> template bypasses this ecosystem limit and achieves ~99% delivery.
              </Alert>
            )}

            {removed && (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                Marked <b>{removed.marked}</b> unreachable contact{removed.marked === 1 ? '' : 's'} (opted out + tagged “{removed.tag || 'Undeliverable'}”) — they'll be skipped in future broadcasts.
                {removed.protectedSellers > 0 && <> {removed.protectedSellers} seller{removed.protectedSellers === 1 ? '' : 's'} with a business/products were kept.</>}
              </Alert>
            )}
            {confirming && !removed && (
              <Alert severity="warning" sx={{ mb: 1.5 }}
                action={
                  <Stack direction="row" spacing={1}>
                    <Button size="small" color="inherit" onClick={() => setConfirming(false)} disabled={removing}>Cancel</Button>
                    <Button size="small" variant="contained" color="warning" onClick={markUnreachable} disabled={removing}>
                      {removing ? 'Marking…' : `Mark ${eligible} unreachable`}
                    </Button>
                  </Stack>
                }>
                Opt out the <b>{eligible}</b> empty-profile number{eligible === 1 ? '' : 's'} and tag them “Undeliverable”, so future broadcasts skip them. Sellers with a business/products are kept. This is reversible.
              </Alert>
            )}
            <TableContainer sx={{ maxHeight: 420, overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Table stickyHeader size="small" sx={{ minWidth: 520 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 800, fontSize: 11.5, bgcolor: 'background.paper' } }}>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Business</TableCell>
                    <TableCell align="center"><Package size={13} style={{ verticalAlign: 'middle' }} /> Products</TableCell>
                    <TableCell><AlertTriangle size={13} style={{ verticalAlign: 'middle' }} /> Failure reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => {
                    const empty = !r.businessCount && !r.productCount;
                    return (
                      <TableRow key={r.userId || `${r.phone}-${i}`} hover>
                        <TableCell sx={{ fontSize: 12.5, fontWeight: 600 }}>{r.name || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{r.phone || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>
                          {r.businessName
                            ? r.businessName
                            : <Typography component="span" sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>No business</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 12.5, fontWeight: 700, color: empty ? 'text.disabled' : 'text.primary' }}>
                          {r.productCount}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: theme.palette.error.main }}>
                          {r.errorReason
                            ? <>
                                {r.errorReason}
                                {r.errorCode ? <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled', ml: 0.5 }}>#{r.errorCode}</Typography> : null}
                              </>
                            : <Typography component="span" sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>—</Typography>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1.25 }}>
              Rows with no business and 0 products are likely incomplete profiles — candidates to remove or remind to finish setup.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={waDialogActionsSx}>
        <Button onClick={onClose} color="inherit">Close</Button>
        <Button variant="outlined" onClick={exportCsv} disabled={loading || !!error || rows.length === 0} startIcon={<Download size={15} />}>
          Download CSV
        </Button>
        {eligible > 0 && !removed && (
          <Button variant="contained" color="warning" startIcon={<UserX size={15} />}
            onClick={() => setConfirming(true)}
            disabled={loading || !!error || confirming || removing}>
            Mark unreachable ({eligible})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const NewBroadcastDialog = ({ open, onClose, onCreate, onSave, editing = null, templates = [], audiences = [] }) => {
  const theme = useTheme();
  const mobile = useWaMobile();
  const approved = useMemo(() => templates.filter((t) => String(t.status || '').toUpperCase() === 'APPROVED'), [templates]);
  const blank = { name: '', template: '', audience: [], when: 'now', scheduledAt: '', skipAlreadySent: true };
  const [form, setForm] = useState(blank);
  const isEdit = !!editing;
  // Lock content fields ONLY when the broadcast is actively running (sending/paused).
  // completed and failed broadcasts are fully editable — saving re-schedules a fresh run.
  const contentLocked = isEdit && ['sending', 'paused'].includes(editing?.status);

  // Prefill from the broadcast being edited (or reset to blank for create) each
  // time the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const isScheduledFuture = editing.status === 'scheduled' && editing.scheduledAt && new Date(editing.scheduledAt).getTime() > Date.now();
      const defaultDate = isScheduledFuture && editing.scheduledAt
        ? toLocalInput(editing.scheduledAt)
        : toLocalInput(new Date(Date.now() + 3600000).toISOString());
      const selectedTemplate = editing.templateName || editing.template_name || editing.template || '';
      setForm({
        name: editing.name || '',
        template: selectedTemplate,
        audience: Array.isArray(editing.audience) ? editing.audience : [],
        when: isScheduledFuture ? 'later' : 'now',
        scheduledAt: defaultDate,
        skipAlreadySent: editing.skipAlreadySent !== false,
      });
    } else {
      setForm({
        ...blank,
        scheduledAt: toLocalInput(new Date(Date.now() + 3600000).toISOString()),
        template: approved[0]?.name || templates[0]?.name || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  // Default the template select to the first approved template once they load (CREATE mode only).
  useEffect(() => {
    if (open && !editing && !form.template && (approved[0]?.name || templates[0]?.name)) {
      setForm((f) => ({ ...f, template: approved[0]?.name || templates[0]?.name }));
    }
  }, [open, editing, approved, templates, form.template]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.name === form.template) || approved.find((t) => t.name === form.template),
    [templates, approved, form.template]
  );

  // Live reach preview from the backend ({ total, willReceive, skipped }), so the
  // admin sees how many NEW contacts will actually get this template.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const segments = useMemo(() => audiences.filter((a) => a.kind === 'segment'), [audiences]);
  const tagAudiences = useMemo(() => audiences.filter((a) => a.kind !== 'segment'), [audiences]);

  const toggleTag = (tag) =>
    setForm((f) => ({ ...f, audience: f.audience.includes(tag) ? f.audience.filter((t) => t !== tag) : [...f.audience, tag] }));

  // Estimated reach = sum of the selected audiences' counts. The backend dedupes
  // by phone at send time, so overlapping selections may estimate a little high.
  const reach = useMemo(
    () => audiences.filter((a) => form.audience.includes(a.key)).reduce((s, a) => s + (a.count || 0), 0),
    [form.audience, audiences],
  );

  // Ask the backend for the real reach whenever the audience, template, or skip
  // toggle changes (debounced). This is what surfaces "70 will receive, 50 skipped".
  useEffect(() => {
    if (!open || !form.audience.length) { setPreview(null); return; }
    let alive = true;
    setPreviewing(true);
    const t = setTimeout(() => {
      waApi.previewBroadcast({ audience: form.audience, templateName: form.template, skipAlreadySent: form.skipAlreadySent })
        .then((d) => { if (alive) setPreview(d); })
        .catch(() => { if (alive) setPreview(null); })
        .finally(() => { if (alive) setPreviewing(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [open, form.audience, form.template, form.skipAlreadySent]);

  const AudienceChip = (a) => {
    const selected = form.audience.includes(a.key);
    const color = a.color || '#6366F1';
    return (
      <Chip key={a.key} label={`${a.label} (${a.count})`} onClick={() => toggleTag(a.key)}
        icon={a.kind === 'segment' ? <Users size={13} /> : undefined}
        variant={selected ? 'filled' : 'outlined'}
        sx={{
          fontWeight: 700, bgcolor: selected ? alpha(color, 0.18) : 'transparent',
          color: selected ? color : 'text.secondary', borderColor: alpha(color, 0.5),
          '& .MuiChip-icon': { color: selected ? color : 'text.disabled' }
        }} />
    );
  };

  const submit = () => {
    const payload = {
      name: form.name.trim(), templateName: form.template, audience: form.audience,
      language: (selectedTemplate?.language) || approved.find((t) => t.name === form.template)?.language || 'en_US',
      when: form.when, scheduledAt: form.scheduledAt, skipAlreadySent: form.skipAlreadySent,
      reach: preview?.willReceive ?? reach,
    };
    if (isEdit) onSave(editing.id, payload);   // PUT /broadcasts/:id
    else onCreate(payload);                    // POST /broadcasts (throttled send)
    setForm(blank);
    onClose();
  };

  const isScheduledValid = form.when === 'later' ? (Boolean(form.scheduledAt) && new Date(form.scheduledAt).getTime() > Date.now() - 60000) : true;
  const valid = Boolean(form.name.trim()) && Boolean(form.template) && form.audience.length > 0 && isScheduledValid;

  // Build full menu options combining approved templates + current form template if set
  const menuOptions = useMemo(() => {
    const list = [...approved];
    if (form.template && !list.some((t) => t.name === form.template)) {
      const extra = templates.find((t) => t.name === form.template);
      list.unshift(extra || { name: form.template, category: 'UTILITY', status: 'APPROVED' });
    }
    return list;
  }, [approved, templates, form.template]);

  return (
    <Dialog open={open} onClose={onClose} {...waDialogProps(mobile, 'sm')}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {isEdit
          ? (contentLocked ? 'Edit Broadcast' : (editing?.status === 'completed' || editing?.status === 'failed') ? 'Edit & Reschedule Broadcast' : 'Edit Broadcast')
          : 'New Broadcast'}
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <TextField label="Campaign name" size="small" fullWidth value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />

          <Box>
            <TextField select label="Approved template" size="small" fullWidth value={form.template}
              onChange={(e) => setForm((f) => ({ ...f, template: e.target.value }))}
              disabled={contentLocked}
              helperText={contentLocked ? 'Template is locked once a broadcast has started.' : (menuOptions.length ? 'Only Meta-approved templates can be broadcast.' : 'No approved templates yet — create and get one approved first.')}>
              {menuOptions.length === 0
                ? <MenuItem value="" disabled>No approved templates</MenuItem>
                : menuOptions.map((t) => <MenuItem key={t.id || t.name} value={t.name}>{t.name} ({t.category || 'UTILITY'})</MenuItem>)}
            </TextField>

            {form.template && (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', mb: 0.75, letterSpacing: 0.5 }}>
                  SELECTED TEMPLATE PREVIEW
                </Typography>
                {selectedTemplate ? (
                  <WaTemplatePreview template={selectedTemplate} compact />
                ) : (
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}` }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.main' }}>
                      {form.template}
                    </Typography>
                  </Box>
                )}

                {selectedTemplate?.category === 'MARKETING' && (
                  <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>
                    <b>Marketing Category Notice:</b> Meta applies per-user frequency limits (Error #131049) to <b>MARKETING</b> templates. Meta drops delivery for recipients who receive heavy marketing from other businesses. For seller onboarding or updates where 99%+ delivery is critical, consider using an approved <b>UTILITY</b> template instead.
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ opacity: contentLocked ? 0.5 : 1, pointerEvents: contentLocked ? 'none' : 'auto' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Audience</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 1.25 }}>
              Sellers &amp; Buyers come straight from your user base — no contact import needed. Tags target saved WhatsApp contacts.
            </Typography>

            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', mb: 0.75 }}>USER SEGMENTS</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: tagAudiences.length ? 1.75 : 0 }}>
              {segments.length ? segments.map(AudienceChip)
                : <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>No users with phone numbers found.</Typography>}
            </Box>

            {tagAudiences.length > 0 && (
              <>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', mb: 0.75 }}>CONTACT TAGS</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tagAudiences.map(AudienceChip)}
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ opacity: contentLocked ? 0.5 : 1, pointerEvents: contentLocked ? 'none' : 'auto' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>When</Typography>
            <ToggleButtonGroup exclusive size="small" value={form.when}
              onChange={(e, v) => {
                if (!v) return;
                setForm((f) => {
                  const updated = { ...f, when: v };
                  if (v === 'later' && (!f.scheduledAt || new Date(f.scheduledAt).getTime() <= Date.now())) {
                    updated.scheduledAt = toLocalInput(new Date(Date.now() + 3600000).toISOString());
                  }
                  return updated;
                });
              }}
              sx={{ width: '100%', '& .MuiToggleButton-root': { flex: 1, fontSize: { xs: 12, sm: 13 }, px: { xs: 1, sm: 2 } } }}>
              <ToggleButton value="now"><Send size={14} style={{ marginRight: 6 }} />Send now</ToggleButton>
              <ToggleButton value="later"><Calendar size={14} style={{ marginRight: 6 }} />Schedule</ToggleButton>
            </ToggleButtonGroup>
            {form.when === 'later' && (
              <TextField type="datetime-local" size="small" fullWidth sx={{ mt: 1.5 }} label="Broadcast Schedule Date & Time" value={form.scheduledAt}
                onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
                InputLabelProps={{ shrink: true }}
                onClick={(e) => e.target.showPicker?.()}
                error={Boolean(form.scheduledAt && new Date(form.scheduledAt).getTime() <= Date.now() - 60000)}
                helperText={form.scheduledAt && new Date(form.scheduledAt).getTime() <= Date.now() - 60000 ? 'Please select a future date and time' : 'Pick when to send broadcast'} />
            )}
          </Box>

          {/* Skip contacts already sent this template (default on). Avoids paying
              to re-message — and annoying — people who already got this campaign. */}
          <Box sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 1)}`, opacity: contentLocked ? 0.5 : 1, pointerEvents: contentLocked ? 'none' : 'auto' }}>
            <FormControlLabel
              sx={{ m: 0, alignItems: 'flex-start' }}
              control={<Switch size="small" checked={form.skipAlreadySent}
                onChange={(e) => setForm((f) => ({ ...f, skipAlreadySent: e.target.checked }))} sx={{ mt: 0.25 }} />}
              label={(
                <Box sx={{ ml: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <UserCheck size={15} color={theme.palette.success.main} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Skip contacts already sent this template</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                    Only message contacts in this audience who never received this template, so a tag that grew since your last send reaches just the new people.
                  </Typography>
                </Box>
              )}
            />
          </Box>

          {/* Info note for sending/paused broadcasts — name-only edit while running */}
          {isEdit && contentLocked && (
            <Stack direction="row" spacing={1} alignItems="flex-start"
              sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.08) }}>
              <AlertTriangle size={15} color={theme.palette.warning.dark} style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11.5, color: theme.palette.warning.dark }}>
                This broadcast is <b>currently running</b> — only the <b>campaign name</b> can be changed. Pause it first to edit content.
              </Typography>
            </Stack>
          )}

          {/* Info note for completed/failed — editing reschedules as a fresh run */}
          {isEdit && !contentLocked && editing?.status && !['draft', 'scheduled'].includes(editing.status) && (
            <Stack direction="row" spacing={1} alignItems="flex-start"
              sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.08) }}>
              <AlertTriangle size={15} color={theme.palette.success.dark} style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11.5, color: theme.palette.success.dark }}>
                Saving will <b>reschedule this as a new run</b> with the updated audience and settings. Previous send history is kept.
              </Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center"
            sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.08) }}>
            <Users size={18} color={theme.palette.info.main} style={{ flexShrink: 0 }} />
            {previewing ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Calculating reach…</Typography>
              </Stack>
            ) : preview ? (
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                <span style={{ color: theme.palette.info.dark }}>{preview.willReceive}</span> will receive
                {form.skipAlreadySent && preview.skipped > 0 && (
                  <span style={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                    {' · '}{preview.skipped} skipped (already sent)
                  </span>
                )}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                Estimated reach: <span style={{ color: theme.palette.info.dark }}>{reach}</span> recipient(s)
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="flex-start"
            sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <AlertTriangle size={15} color={theme.palette.warning.dark} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11.5, color: theme.palette.warning.dark }}>
              Sends are throttled to your WhatsApp messaging tier and only reach opted-in contacts. Each message is billable.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={waDialogActionsSx}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!valid} onClick={submit} startIcon={<Send size={15} />}>
          {isEdit ? 'Save changes' : (form.when === 'now' ? 'Send Broadcast' : 'Schedule Broadcast')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WhatsAppBroadcasts = () => {
  const theme = useTheme();
  const mobile = useWaMobile();
  const [broadcasts, setBroadcasts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // broadcast being edited (null = create)
  const [toast, setToast] = useState('');
  const [pending, setPending] = useState(null); // payload awaiting confirmation
  const [sending, setSending] = useState(false);
  const [failedFor, setFailedFor] = useState(null); // broadcast whose failed report is open

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([
      waApi.getBroadcasts(),
      waApi.getTemplates(),
      waApi.getAudiences(),
    ])
      .then(([bRows, tRows, aRows]) => {
        if (Array.isArray(bRows)) setBroadcasts(bRows);
        if (Array.isArray(tRows)) setTemplates(tRows);
        if (Array.isArray(aRows)) setAudiences(aRows);
      })
      .catch(() => setError('Could not load broadcasts. Check your connection and retry.'))
      .finally(() => setLoading(false));
  };

  // Last-seen status per broadcast, so we fire a notification exactly once when
  // one crosses into a terminal state (rather than on every progress tick).
  const statusRef = useRef({});

  useEffect(() => {
    load();
    // Live progress as the backend dispatches a broadcast.
    return onWaEvent('wa:broadcast', (b) => {
      const prevStatus = statusRef.current[b.id];
      statusRef.current[b.id] = b.status;
      if (prevStatus && prevStatus !== b.status) {
        if (b.status === 'completed') {
          notify({
            title: 'Broadcast complete',
            message: `“${b.name}” finished — ${b.sent} sent${b.failed ? `, ${b.failed} failed` : ''}`,
            severity: b.failed ? 'warning' : 'success',
            system: true,
            tag: `wa-broadcast-${b.id}`,
            url: '/whatsapp',
          });
        } else if (b.status === 'failed') {
          notify({
            title: 'Broadcast failed',
            message: `“${b.name}” could not be sent`,
            severity: 'error',
            system: true,
            tag: `wa-broadcast-${b.id}`,
            url: '/whatsapp',
          });
        }
      }
      setBroadcasts((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...b } : x)));
    });
  }, []);

  // The dialog hands the payload here; we confirm before the billable send.
  const requestCreate = (payload) => setPending(payload);

  const confirmCreate = async () => {
    const payload = pending;
    setSending(true);
    try {
      const b = await waApi.createBroadcast(payload);
      setBroadcasts((p) => [b, ...p]);
      setToast(payload.when === 'now' ? 'Broadcast started' : 'Broadcast scheduled');
    } catch (e) {
      setToast('Could not start the broadcast. Please try again.');
    } finally {
      setSending(false);
      setPending(null);
    }
  };

  // Patch a single broadcast in state (the wa:broadcast socket also keeps it live).
  const patch = (b) => b && setBroadcasts((p) => p.map((x) => (x.id === b.id ? { ...x, ...b } : x)));

  // Open the dialog for a fresh broadcast or to edit an existing (scheduled) one.
  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setOpen(true); };
  const closeDialog = () => { setOpen(false); setEditing(null); };

  // Save edits to a broadcast.
  const handleSave = async (id, payload) => {
    try {
      const updated = await waApi.updateBroadcast(id, payload);
      patch(updated);
      setToast(payload.when === 'now' ? 'Broadcast sending started' : 'Broadcast rescheduled successfully!');
    } catch (e) {
      setToast(e?.response?.data?.msg || 'Could not update broadcast. Please try again.');
    }
  };

  const handlePause = async (id) => {
    try { patch(await waApi.pauseBroadcast(id)); setToast('Broadcast paused'); }
    catch (e) { setToast(e?.response?.data?.msg || 'Could not pause. Please try again.'); }
  };
  const handleResume = async (id) => {
    try { patch(await waApi.resumeBroadcast(id)); setToast('Broadcast resumed'); }
    catch (e) { setToast(e?.response?.data?.msg || 'Could not resume. Please try again.'); }
  };

  // Restart re-sends to everyone (billable) and Delete is irreversible → confirm both.
  const [confirm, setConfirm] = useState(null); // { type: 'restart'|'delete', broadcast }
  const [busy, setBusy] = useState(false);
  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.type === 'restart') { patch(await waApi.restartBroadcast(confirm.broadcast.id)); setToast('Broadcast restarted'); }
      else { await waApi.deleteBroadcast(confirm.broadcast.id); setBroadcasts((p) => p.filter((x) => x.id !== confirm.broadcast.id)); setToast('Broadcast deleted'); }
      setConfirm(null);
    } catch (e) {
      setToast(e?.response?.data?.msg || 'Action failed. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Box>
      <WaSectionHeader
        title="Broadcasts"
        subtitle="Send an approved template to a segment of contacts, now or scheduled."
        action={<Button variant="contained" sx={{ width: { xs: '100%', sm: 'auto' } }} startIcon={<Plus size={16} />} onClick={openCreate}>New Broadcast</Button>}
      />

      {loading && (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 6 }} key={i} sx={waGridItem}>
              <Card sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Skeleton variant="rounded" width={38} height={38} />
                    <Box>
                      <Skeleton width={120} height={18} />
                      <Skeleton width={80} height={13} />
                    </Box>
                  </Stack>
                  <Skeleton variant="rounded" width={64} height={22} />
                </Stack>
                <Skeleton variant="rounded" height={7} sx={{ mt: 1.5, borderRadius: 5 }} />
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-around">
                  {Array.from({ length: 4 }).map((__, j) => (
                    <Box key={j} sx={{ textAlign: 'center', flex: 1 }}>
                      <Skeleton width="50%" height={18} sx={{ mx: 'auto' }} />
                      <Skeleton width="60%" height={12} sx={{ mx: 'auto' }} />
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && broadcasts.length === 0 && (
        <Card sx={{ p: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', boxShadow: 'none' }}>
          <Megaphone size={28} color={theme.palette.text.disabled} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 1 }}>No broadcasts yet</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>
            Create a broadcast to send an approved template to a tagged audience.
          </Typography>
        </Card>
      )}

      <Grid container spacing={2}>
        {!loading && broadcasts.map((b) => {
          const meta = BROADCAST_STATUS[b.status] || { label: b.status, color: 'default' };
          const pct = b.total ? Math.round((b.sent / b.total) * 100) : 0;
          return (
            <Grid size={{ xs: 12, sm: 6, md: 6 }} key={b.id} sx={waGridItem}>
              <Card sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
                  sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 0.75, sm: 1 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{
                      width: 38, height: 38, borderRadius: 2, bgcolor: alpha(theme.palette.secondary.main, 0.12),
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <Megaphone size={18} color={theme.palette.secondary.main} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 14, fontWeight: 800 }}>{b.name}</Typography>
                      <Typography noWrap sx={{ fontSize: 11.5, color: 'text.disabled' }}>Template: {b.templateName}</Typography>
                    </Box>
                  </Stack>
                  <Chip label={meta.label} size="small" color={meta.color === 'default' ? undefined : meta.color}
                    variant={meta.color === 'default' ? 'outlined' : 'filled'} sx={{ fontWeight: 700, flexShrink: 0 }} />
                </Stack>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 1.25, width: '100%' }}>
                  {(b.audience || []).map((a) => (
                    <Chip key={a} label={a} size="small"
                      sx={{
                        height: 18, fontSize: 9.5, maxWidth: { xs: 110, sm: 'none' },
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                      }} />
                  ))}
                  <Typography sx={{ fontSize: 11, color: 'text.disabled', ml: 'auto', flexShrink: 0 }}>{fmtDateTime(b.scheduledAt)}</Typography>
                </Box>

                {(b.status === 'sending' || b.status === 'completed' || b.status === 'paused') && (
                  <Box sx={{ mt: 1.5 }}>
                    <LinearProgress variant="determinate" value={pct}
                      sx={{
                        height: 7, borderRadius: 5, bgcolor: theme.palette.background.neutral,
                        '& .MuiLinearProgress-bar': { bgcolor: b.status === 'completed' ? theme.palette.success.main : b.status === 'paused' ? theme.palette.text.disabled : theme.palette.warning.main }
                      }} />
                    <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.5 }}>{b.sent} / {b.total} sent ({pct}%){b.status === 'paused' ? ' · paused' : ''}</Typography>
                  </Box>
                )}

                <Divider sx={{ mt: 'auto', mb: 1.5 }} />
                <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} sx={{ minWidth: 0 }}>
                  <StatCell label="Sent" value={b.sent} color={theme.palette.text.primary} />
                  <StatCell label="Delivered" value={b.delivered} color={theme.palette.info.main} />
                  <StatCell label="Read" value={b.read} color={theme.palette.success.main} />
                  <StatCell label="Failed" value={b.failed} color={theme.palette.error.main}
                    onClick={b.failed > 0 ? () => setFailedFor(b) : undefined} />
                </Stack>

                {/* Lifecycle controls — stop / continue / restart / delete / edit. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.25, sm: 0.5 }, justifyContent: 'flex-end', mt: 1 }}>
                  {/* Edit is always available — name can always be changed; template/audience locked once sending */}
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(b)}>
                      <Pencil size={16} />
                    </IconButton>
                  </Tooltip>
                  {b.status === 'sending' && (
                    <Tooltip title="Pause"><IconButton size="small" onClick={() => handlePause(b.id)}><Pause size={16} /></IconButton></Tooltip>
                  )}
                  {b.status === 'paused' && (
                    <Tooltip title="Continue"><IconButton size="small" color="primary" onClick={() => handleResume(b.id)}><Play size={16} /></IconButton></Tooltip>
                  )}
                  {b.status !== 'sending' && b.status !== 'scheduled' && (
                    <Tooltip title="Start again"><IconButton size="small" onClick={() => setConfirm({ type: 'restart', broadcast: b })}><RotateCcw size={16} /></IconButton></Tooltip>
                  )}
                  {b.status !== 'sending' && (
                    <Tooltip title="Delete"><IconButton size="small" sx={{ color: 'error.main' }} onClick={() => setConfirm({ type: 'delete', broadcast: b })}><Trash2 size={16} /></IconButton></Tooltip>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <NewBroadcastDialog
        open={open}
        editing={editing}
        onClose={closeDialog}
        onCreate={(payload) => { closeDialog(); requestCreate(payload); }}
        onSave={handleSave}
        templates={templates}
        audiences={audiences}
      />

      {/* Final confirmation — broadcasts are billable and irreversible. */}
      <Dialog open={!!pending} onClose={() => !sending && setPending(null)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {pending?.when === 'now' ? 'Send this broadcast now?' : 'Schedule this broadcast?'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 13.5 }}><b>Campaign:</b> {pending?.name}</Typography>
            <Typography sx={{ fontSize: 13.5 }}><b>Template:</b> {pending?.templateName}</Typography>
            <Typography sx={{ fontSize: 13.5 }}><b>Audience:</b> {(pending?.audience || []).join(', ') || '—'}</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start"
              sx={{ p: 1.25, mt: 0.5, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
              <AlertTriangle size={15} color={theme.palette.warning.dark} style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12.5, color: theme.palette.warning.dark }}>
                This will message <b>{pending?.reach || 0}</b> opted-in contact(s). Each message is billable and cannot be unsent.
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={waDialogActionsSx}>
          <Button onClick={() => setPending(null)} color="inherit" disabled={sending}>Cancel</Button>
          <Button variant="contained" onClick={confirmCreate} disabled={sending}
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send size={15} />}>
            {pending?.when === 'now' ? 'Send Broadcast' : 'Schedule Broadcast'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restart / Delete confirmation */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {confirm?.type === 'restart' ? 'Restart this broadcast?' : 'Delete this broadcast?'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            {confirm?.type === 'restart'
              ? <>This re-sends <b>{confirm?.broadcast?.name}</b> to the whole audience again from the start. Each message is billable and cannot be unsent.</>
              : <><b>{confirm?.broadcast?.name}</b> will be removed. This can’t be undone.</>}
          </Typography>
        </DialogContent>
        <DialogActions sx={waDialogActionsSx}>
          <Button onClick={() => setConfirm(null)} color="inherit" disabled={busy}>Cancel</Button>
          <Button variant="contained" color={confirm?.type === 'delete' ? 'error' : 'primary'} onClick={runConfirm} disabled={busy}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : (confirm?.type === 'restart' ? <RotateCcw size={15} /> : <Trash2 size={15} />)}>
            {confirm?.type === 'restart' ? 'Restart' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <FailedReportDialog broadcast={failedFor} onClose={() => setFailedFor(null)}
        onRemoved={(res) => setToast(res?.marked ? `Marked ${res.marked} unreachable — future broadcasts will skip them` : 'No eligible contacts to mark')} />

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={/could not|error|fail/i.test(toast) ? 'error' : 'success'} variant="filled" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppBroadcasts;
